/**
 * Library item building/filtering — pure data logic shared by RightSidebar's Library tab.
 * Chips: View All lists every image and video in the project bucket via GET /{projectId}/image and
 * GET /{projectId}/video; other chips use project JSON references.
 */

import { ProjectPanelRoute, type LibraryMediaHighlight } from "@/types/routes";
import type { BreakdownHistorySnapshot, ProjectImageListItem, ProjectVideoListItem } from "@/api/api";
import { approvedStoryboardShotVideoId, isStoryboardShotPrimarySlotUploadedVideo, posterImageIdForCompletedVideoVisual } from "@/services/storyboard";
import type { ProjectDetailsProject, ProjectDetailsStoryboardShot, ProjectDetailsVisual } from "@/types/project";

export type SectionFilter = "viewAll" | "characters" | "locations" | "shots" | "activeShot" | "videos";

export const VIEW_ALL_CHIP: { value: SectionFilter; label: string } = { value: "viewAll", label: "View All" };

/** Chips after "View All" and the "activeShot" current-scope chip, which are rendered separately. */
export const SECTION_FILTER_CHIPS: Array<{ value: SectionFilter; label: string }> = [
  VIEW_ALL_CHIP,
  { value: "characters", label: "Characters" },
  { value: "locations", label: "Locations" },
  { value: "shots", label: "Images" },
  { value: "videos", label: "Videos" }
];

export interface LibraryItem {
  readonly id: string;
  readonly imageId: string;
  readonly section: "characters" | "locations" | "shots" | "project";
  readonly displayName: string;
  readonly isApproved: boolean;
  /** Shared per-project favorite state; keyed by `libraryVideoId` when set, otherwise `imageId`. */
  readonly isFavorite: boolean;
  /** Character or location id when section is characters/locations; used for active-shot filter. */
  readonly entityId?: string;
  /** Shot index when section is shots; used for active-shot filter. */
  readonly shotIndex?: number;
  /** Completed visual: play this `video_id` (avoids ambiguous map when multiple visuals share a reference). */
  readonly libraryVideoId?: string;
}

export function buildFavoriteImageIdsFromProject(project: ProjectDetailsProject | null): ReadonlySet<string> {
  return new Set(project?.["favorite-images"]);
}

export function buildFavoriteVideoIdsFromProject(project: ProjectDetailsProject | null): ReadonlySet<string> {
  return new Set(project?.["favorite-video-ids"]);
}

export function libraryItemMatchesHighlight(
  item: LibraryItem,
  highlight: LibraryMediaHighlight,
  completedVideoByImageId: ReadonlyMap<string, string>
): boolean {
  if (highlight.kind === "video") {
    const videoId = item.libraryVideoId ?? (item.section === "shots" ? (completedVideoByImageId.get(item.imageId) ?? null) : null);
    return videoId === highlight.mediaId;
  }

  return item.imageId === highlight.mediaId && item.libraryVideoId === undefined;
}

/** Frame ↔ Video are two views onto the same shot — switching between them should not reset the Library's selected chip. */
export function isFrameVideoPanelSwap(previousPanel: string | undefined, nextPanel: string | undefined): boolean {
  const isFrameOrVideo = (panel: string | undefined): boolean => panel === ProjectPanelRoute.ImageFrame || panel === ProjectPanelRoute.Video;
  return isFrameOrVideo(previousPanel) && isFrameOrVideo(nextPanel);
}

export const activeShotScopeNounFromPanel = (panel: string | undefined): "character" | "location" | "shot" => {
  if (panel === ProjectPanelRoute.Characters) {
    return "character";
  }

  if (panel === ProjectPanelRoute.Locations) {
    return "location";
  }

  return "shot";
};

/**
 * All image ids to show for a character or location: `images` (order preserved), then any id in
 * `approved-images` that is not already listed (matches project JSON where both arrays are set).
 */
function mergeCharacterOrLocationImageIds(images: readonly string[] | undefined, approvedImages: readonly string[] | undefined): string[] {
  const ordered: string[] = [];
  const seen = new Set<string>();

  for (const id of images ?? []) {
    if (id === undefined || id === null || id === "") {
      continue;
    }

    if (seen.has(id)) {
      continue;
    }

    seen.add(id);
    ordered.push(id);
  }

  for (const id of approvedImages ?? []) {
    if (id === undefined || id === null || id === "") {
      continue;
    }

    if (seen.has(id)) {
      continue;
    }

    seen.add(id);
    ordered.push(id);
  }

  return ordered;
}

/** `id` is stored as a storyboard `visuals[].video_id` — must not be requested as `/image/{id}` (library image rows). */
function shotImagesEntryIsVideoAssetId(shot: ProjectDetailsStoryboardShot, id: string): boolean {
  return (shot.visuals ?? []).some((v) => typeof v.video_id === "string" && v.video_id !== "" && v.video_id === id);
}

export function buildLibraryItems(project: ProjectDetailsProject | null, favoriteImageIds: ReadonlySet<string> = new Set()): LibraryItem[] {
  if (!project) {
    return [];
  }

  const items: LibraryItem[] = [];
  const seen = new Set<string>();

  const characters = project.characters ?? [];
  for (const entity of characters) {
    const approvedArr = entity["approved-images"];
    const approved = approvedArr === undefined || approvedArr === null ? new Set<string>() : new Set(approvedArr);
    const imageIds = mergeCharacterOrLocationImageIds(entity.images, approvedArr);

    for (const imageId of imageIds) {
      const key = `characters-${entity.id}-${imageId}`;

      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      items.push({
        id: key,
        imageId,
        section: "characters",
        displayName: entity.name ?? "",
        isApproved: approved.has(imageId),
        isFavorite: favoriteImageIds.has(imageId),
        entityId: entity.id
      });
    }
  }

  const locations = project.locations ?? [];
  for (const entity of locations) {
    const approvedArr = entity["approved-images"];
    const approved = approvedArr === undefined || approvedArr === null ? new Set<string>() : new Set(approvedArr);
    const imageIds = mergeCharacterOrLocationImageIds(entity.images, approvedArr);

    for (const imageId of imageIds) {
      const key = `locations-${entity.id}-${imageId}`;

      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      items.push({
        id: key,
        imageId,
        section: "locations",
        displayName: entity.name ?? "",
        isApproved: approved.has(imageId),
        isFavorite: favoriteImageIds.has(imageId),
        entityId: entity.id
      });
    }
  }

  const shots = project["storyboard-shots"] ?? [];
  for (let shotIndex = 0; shotIndex < shots.length; shotIndex++) {
    const shot = shots[shotIndex];

    if (shot === undefined || shot === null) {
      continue;
    }

    const images = shot.images ?? [];
    const approvedArr = shot["approved-timeline-images"];
    const approved = approvedArr === undefined || approvedArr === null ? new Set<string>() : new Set(approvedArr);
    const name = shot["shot-description"] || shot["shot-name"] || shot["shot-id"] || `Shot ${shotIndex + 1}`;

    const primarySlotId = images[0];
    for (const imageId of images) {
      if (imageId === undefined || imageId === null) {
        continue;
      }

      /** `images[0]` is a video file id for user uploads — do not list it as a shot “image” row (no `/image` entity). */
      if (typeof primarySlotId === "string" && primarySlotId !== "" && imageId === primarySlotId && isStoryboardShotPrimarySlotUploadedVideo(shot)) {
        continue;
      }

      /** Video uuid can appear in `images` at any index (reorders / legacy); never treat as an image entity. */
      if (shotImagesEntryIsVideoAssetId(shot, imageId)) {
        continue;
      }

      const key = `shots-${shotIndex}-${imageId}`;

      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      items.push({
        id: key,
        imageId,
        section: "shots",
        displayName: name,
        isApproved: approved.has(imageId),
        isFavorite: favoriteImageIds.has(imageId),
        shotIndex
      });
    }
  }

  return items;
}

export function isCompletedVisualWithPlaybackRefs(v: ProjectDetailsVisual, posterImageId?: string): boolean {
  if (v.status !== "completed") {
    return false;
  }

  const refId = posterImageId ?? v["reference_image_id"];
  const vid = v["video_id"];

  return typeof refId === "string" && refId !== "" && typeof vid === "string" && vid !== "";
}

/** One library row per completed visual on a storyboard shot (all shots — for “Shots: Videos” / Active video). */
export function buildStoryboardVideoLibraryItems(
  project: ProjectDetailsProject | null,
  favoriteVideoIds: ReadonlySet<string> = new Set()
): LibraryItem[] {
  if (!project) {
    return [];
  }

  const items: LibraryItem[] = [];
  const shots = project["storyboard-shots"] ?? [];

  for (let shotIndex = 0; shotIndex < shots.length; shotIndex++) {
    const shot = shots[shotIndex];

    if (shot === undefined || shot === null) {
      continue;
    }

    const approvedVideoId = approvedStoryboardShotVideoId(shot);
    const name = shot["shot-description"] || shot["shot-name"] || shot["shot-id"] || `Shot ${shotIndex + 1}`;

    for (const v of shot.visuals ?? []) {
      const posterImageId = posterImageIdForCompletedVideoVisual(shot, v);
      if (!isCompletedVisualWithPlaybackRefs(v, posterImageId)) {
        continue;
      }

      const vid = v["video_id"] as string;
      const key = `shot-video-${shotIndex}-${vid}`;

      items.push({
        id: key,
        imageId: posterImageId,
        section: "shots",
        displayName: name,
        isApproved: vid === approvedVideoId,
        isFavorite: favoriteVideoIds.has(vid),
        shotIndex,
        libraryVideoId: vid
      });
    }
  }

  return items;
}

export interface FilterItemsByActiveShotContext {
  readonly project: ProjectDetailsProject | null;
  readonly activeShotIndex: number;
  readonly panel: string | undefined;
  readonly selectedCharacterId: string | null | undefined;
  readonly selectedLocationId: string | null | undefined;
}

/** Section filters where library rows are not narrowed to a single character/location entity. */
export function skipEntityNarrowForSection(sectionFilter: SectionFilter): boolean {
  return (
    sectionFilter === "viewAll" ||
    sectionFilter === "characters" ||
    sectionFilter === "locations" ||
    sectionFilter === "shots" ||
    sectionFilter === "videos" ||
    sectionFilter === "activeShot"
  );
}

/** Approved image ids from project JSON (characters, locations, shots) for badges on S3-listed rows. */
export function buildApprovedImageIdsFromJsonItems(jsonImageItems: readonly LibraryItem[]): ReadonlySet<string> {
  const approved = new Set<string>();

  for (const item of jsonImageItems) {
    if (item.isApproved) {
      approved.add(item.imageId);
    }
  }

  return approved;
}

/** Approved video ids across all storyboard shots, for badges on S3-listed video rows. */
export function buildApprovedVideoIdsFromProject(project: ProjectDetailsProject | null): ReadonlySet<string> {
  const approved = new Set<string>();

  for (const shot of project?.["storyboard-shots"] ?? []) {
    if (shot === undefined || shot === null) {
      continue;
    }

    const videoId = approvedStoryboardShotVideoId(shot);
    if (videoId !== null) {
      approved.add(videoId);
    }
  }

  return approved;
}

/**
 * A video's own entity metadata only carries `poster_image_id`/`extractedPosterImageId`, which is
 * empty for videos that never got a poster extracted (e.g. text-only Omni edits) - project JSON's
 * per-shot `visuals[].reference_image_id` is the only other source of a usable thumbnail/drag id
 * for those, so build a video_id -> image id fallback map from it.
 */
export function buildVideoReferenceImageIdMap(project: ProjectDetailsProject | null): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  if (!project) {
    return map;
  }

  for (const shot of project["storyboard-shots"] ?? []) {
    for (const v of shot?.visuals ?? []) {
      const vid = v["video_id"] as string | undefined;
      if (typeof vid !== "string" || vid === "" || map.has(vid)) {
        continue;
      }

      const refId = posterImageIdForCompletedVideoVisual(shot, v).trim();
      if (refId !== "") {
        map.set(vid, refId);
      }
    }
  }

  return map;
}

export function buildVideoLibraryItemsFromProjectMedia(
  videos: readonly ProjectVideoListItem[],
  approvedVideoIds: ReadonlySet<string>,
  fallbackImageIdByVideoId: ReadonlyMap<string, string> = new Map(),
  favoriteVideoIds: ReadonlySet<string> = new Set()
): LibraryItem[] {
  return videos.map((vid) => {
    const extractedPosterId = vid.extractedPosterImageId?.trim() ?? "";
    const referenceId = vid.posterImageId?.trim() ?? "";
    const thumbnailImageId = extractedPosterId.length > 0 ? extractedPosterId : referenceId || (fallbackImageIdByVideoId.get(vid.id) ?? "");

    return {
      id: `project-video-${vid.id}`,
      imageId: thumbnailImageId,
      section: "project",
      displayName: vid.displayName,
      isApproved: approvedVideoIds.has(vid.id),
      isFavorite: favoriteVideoIds.has(vid.id),
      libraryVideoId: vid.id
    };
  });
}

/** View All: every image and video id under the project prefix in S3 (via list entity APIs). */
export function buildViewAllLibraryItemsFromProjectMedia(
  images: readonly ProjectImageListItem[],
  videos: readonly ProjectVideoListItem[],
  approvedImageIds: ReadonlySet<string>,
  approvedVideoIds: ReadonlySet<string>,
  options: {
    fallbackImageIdByVideoId?: ReadonlyMap<string, string>;
    favoriteImageIds?: ReadonlySet<string>;
    favoriteVideoIds?: ReadonlySet<string>;
  } = {}
): LibraryItem[] {
  const { fallbackImageIdByVideoId = new Map(), favoriteImageIds = new Set(), favoriteVideoIds = new Set() } = options;
  const dated: Array<{ readonly item: LibraryItem; readonly lastModifiedMs: number | undefined }> = [];
  const extractedPosterImageIds = new Set(videos.map((v) => v.extractedPosterImageId?.trim() ?? "").filter((id) => id.length > 0));

  for (const img of images) {
    if (extractedPosterImageIds.has(img.id)) {
      continue;
    }

    dated.push({
      item: {
        id: `project-image-${img.id}`,
        imageId: img.id,
        section: "project",
        displayName: img.displayName,
        isApproved: approvedImageIds.has(img.id),
        isFavorite: favoriteImageIds.has(img.id)
      },
      lastModifiedMs: img.lastModifiedMs
    });
  }

  const videoItems = buildVideoLibraryItemsFromProjectMedia(videos, approvedVideoIds, fallbackImageIdByVideoId, favoriteVideoIds);
  for (const [index, item] of videoItems.entries()) {
    dated.push({ item, lastModifiedMs: videos[index]?.lastModifiedMs });
  }

  return dated
    .toSorted((a, b) => (b.lastModifiedMs ?? 0) - (a.lastModifiedMs ?? 0))
    .map(({ item }) => item);
}

function shotLibraryImageItemsAtIndex(imageItems: LibraryItem[], shotIndex: number): LibraryItem[] {
  return imageItems.filter((item) => item.section === "shots" && item.shotIndex === shotIndex);
}

/**
 * Active chip: Characters → selected character images; Locations → selected location images;
 * Frame → active storyboard shot images only; Video → the shot's completed visuals only, since a still
 * row there would carry the shot's *frame* approval next to videos; other storyboard tools → active shot images.
 */
export function filterItemsByActiveShot(
  imageItems: LibraryItem[],
  videoVisualItems: LibraryItem[],
  ctx: FilterItemsByActiveShotContext
): LibraryItem[] {
  const { project, activeShotIndex, panel, selectedCharacterId, selectedLocationId } = ctx;

  if (panel === ProjectPanelRoute.Characters) {
    if (selectedCharacterId === undefined || selectedCharacterId === null || selectedCharacterId === "") {
      return [];
    }

    return imageItems.filter((item) => item.section === "characters" && item.entityId === selectedCharacterId);
  }

  if (panel === ProjectPanelRoute.Locations) {
    if (selectedLocationId === undefined || selectedLocationId === null || selectedLocationId === "") {
      return [];
    }

    return imageItems.filter((item) => item.section === "locations" && item.entityId === selectedLocationId);
  }

  const shots = project?.["storyboard-shots"] ?? [];
  if (activeShotIndex < 0 || activeShotIndex >= shots.length) {
    return [];
  }

  if (panel === ProjectPanelRoute.Video) {
    return videoVisualItems.filter((item) => item.shotIndex === activeShotIndex);
  }

  return shotLibraryImageItemsAtIndex(imageItems, activeShotIndex);
}

/** Every image/video id the *current* project JSON references (characters, locations, shots, completed visuals). */
export function buildCurrentlyReferencedMediaIds(imageItems: readonly LibraryItem[], videoVisualItems: readonly LibraryItem[]): ReadonlySet<string> {
  const ids = new Set<string>();

  for (const item of imageItems) {
    ids.add(item.imageId);
  }

  for (const item of videoVisualItems) {
    ids.add(item.libraryVideoId ?? item.imageId);
  }

  return ids;
}

/** Every image/video id a past breakdown snapshot's characters/locations/storyboard-shots reference. */
export function extractMediaIdsFromBreakdownSnapshot(snapshot: BreakdownHistorySnapshot): Set<string> {
  const ids = new Set<string>();

  for (const entity of snapshot.characters ?? []) {
    for (const id of mergeCharacterOrLocationImageIds(entity.images, entity["approved-images"])) {
      ids.add(id);
    }
  }

  for (const entity of snapshot.locations ?? []) {
    for (const id of mergeCharacterOrLocationImageIds(entity.images, entity["approved-images"])) {
      ids.add(id);
    }
  }

  for (const shot of snapshot["storyboard-shots"] ?? []) {
    for (const id of shot.images ?? []) {
      if (typeof id === "string" && id !== "") {
        ids.add(id);
      }
    }

    for (const id of shot["approved-timeline-images"] ?? []) {
      if (typeof id === "string" && id !== "") {
        ids.add(id);
      }
    }

    for (const v of shot.visuals ?? []) {
      if (typeof v.video_id === "string" && v.video_id !== "") {
        ids.add(v.video_id);
      }
    }
  }

  return ids;
}

/** Old character/location text fields a snapshot preserved, for a read-only "Previous breakdown" section. */
export interface BreakdownSnapshotCharacterOrLocationSource {
  readonly kind: "character" | "location";
  readonly snapshotDate: string | null;
  readonly name: string;
  readonly description: string;
  readonly appearance: string;
}

/** Old shot text fields a snapshot preserved, for a read-only "Previous breakdown" section. */
export interface BreakdownSnapshotShotSource {
  readonly kind: "shot";
  readonly snapshotDate: string | null;
  readonly shotDescription: string;
  readonly refImagePrompt: string;
  readonly videoGenPrompt: string;
  readonly notes: string;
  readonly dialogues: string;
}

export type BreakdownSnapshotMediaSource = BreakdownSnapshotCharacterOrLocationSource | BreakdownSnapshotShotSource;

/** What a past breakdown snapshot said about an image/video id (used by DetailsTabPanel). `historySnapshots` must be newest-first. */
export function findBreakdownSnapshotSourceForMediaId(
  historySnapshots: readonly BreakdownHistorySnapshot[],
  mediaId: string | null
): BreakdownSnapshotMediaSource | null {
  if (mediaId === null || mediaId === "") {
    return null;
  }

  for (const snapshot of historySnapshots) {
    for (const character of snapshot.characters ?? []) {
      if (mergeCharacterOrLocationImageIds(character.images, character["approved-images"]).includes(mediaId)) {
        return {
          kind: "character",
          snapshotDate: snapshot.createdDate,
          name: character.name ?? "",
          description: character.description ?? "",
          appearance: character.appearance ?? ""
        };
      }
    }

    for (const location of snapshot.locations ?? []) {
      if (mergeCharacterOrLocationImageIds(location.images, location["approved-images"]).includes(mediaId)) {
        return {
          kind: "location",
          snapshotDate: snapshot.createdDate,
          name: location.name ?? "",
          description: location.description ?? "",
          appearance: location.appearance ?? ""
        };
      }
    }

    for (const shot of snapshot["storyboard-shots"] ?? []) {
      const isImageMatch = (shot.images ?? []).includes(mediaId) || (shot["approved-timeline-images"] ?? []).includes(mediaId);
      const isVideoMatch = (shot.visuals ?? []).some((v) => v.video_id === mediaId);

      if (isImageMatch || isVideoMatch) {
        return {
          kind: "shot",
          snapshotDate: snapshot.createdDate,
          shotDescription: shot["shot-description"] ?? "",
          refImagePrompt: shot["ref-image-prompt"] ?? "",
          videoGenPrompt: shot["video-gen-prompt"] ?? "",
          notes: shot.notes ?? "",
          dialogues: shot.dialogues ?? ""
        };
      }
    }
  }

  return null;
}

/** "Aug 13, 2026" — date-only, no time, for the "Previous breakdown" divider label. */
export function formatBreakdownHistoryDate(iso: string | null): string {
  if (!iso?.trim()) {
    return "unknown date";
  }

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "unknown date";
  }

  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export type LibraryGridEntry =
  | { readonly kind: "item"; readonly item: LibraryItem }
  | { readonly kind: "divider"; readonly id: string; readonly label: string };

/**
 * View All only: rows still referenced by the live project (or unmatched) pass through unchanged;
 * rows only a past breakdown references are moved below a single "Previous breakdowns" divider,
 * combined across every retained snapshot.
 */
export function buildViewAllGridEntriesWithHistory(
  sortedViewAllItems: readonly LibraryItem[],
  currentReferencedIds: ReadonlySet<string>,
  historySnapshots: readonly BreakdownHistorySnapshot[]
): LibraryGridEntry[] {
  const snapshotIdSets = historySnapshots.map((snapshot) => extractMediaIdsFromBreakdownSnapshot(snapshot));
  const currentBucket: LibraryItem[] = [];
  const previousBucket: LibraryItem[] = [];

  for (const item of sortedViewAllItems) {
    const refId = item.libraryVideoId ?? item.imageId;

    if (currentReferencedIds.has(refId)) {
      currentBucket.push(item);
      continue;
    }

    const isFromPastBreakdown = snapshotIdSets.some((ids) => ids.has(refId));
    (isFromPastBreakdown ? previousBucket : currentBucket).push(item);
  }

  const entries: LibraryGridEntry[] = currentBucket.map((item) => ({ kind: "item", item }));

  if (previousBucket.length > 0) {
    entries.push({ kind: "divider", id: "breakdown-history-divider", label: "Previous breakdowns" });
    for (const item of previousBucket) {
      entries.push({ kind: "item", item });
    }
  }

  return entries;
}

/**
 * Characters/Locations tools: for chips other than Characters / Locations / Shots: Images / Shots: Videos / Active,
 * hide other entities’ character (or location) rows so mixed grids stay scoped to the timeline selection.
 */
export function restrictItemsToPanelSelectedEntity(
  items: LibraryItem[],
  panel: string | undefined,
  sectionFilter: SectionFilter,
  selectedCharacterId: string | null | undefined,
  selectedLocationId: string | null | undefined
): LibraryItem[] {
  const skipNarrow = skipEntityNarrowForSection(sectionFilter);

  if (
    panel === ProjectPanelRoute.Characters &&
    !skipNarrow &&
    selectedCharacterId !== undefined &&
    selectedCharacterId !== null &&
    selectedCharacterId !== ""
  ) {
    return items.filter((i) => i.section !== "characters" || i.entityId === selectedCharacterId);
  }

  if (
    panel === ProjectPanelRoute.Locations &&
    !skipNarrow &&
    selectedLocationId !== undefined &&
    selectedLocationId !== null &&
    selectedLocationId !== ""
  ) {
    return items.filter((i) => i.section !== "locations" || i.entityId === selectedLocationId);
  }

  return items;
}
